import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismCard, PrismMessage, PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { t } from 'ember-intl';
import SettingsCustomPropertyTagsCellsTdDefinitionActions from 'lumen/components/settings/custom-property-tags/cells/td-definition-actions';
import SettingsCustomPropertyTagsCellsTdTagType from 'lumen/components/settings/custom-property-tags/cells/td-tag-type';
import type CustomTagDefinition from 'lumen/models/custom-tag-definition';
import { MAX_CUSTOM_TAG_DEFINITIONS } from 'lumen/utils/constants/property-tags';

interface Signature {
  Args: {
    definitions: CustomTagDefinition[];
    isLoading: boolean;
    onDelete: (definition: CustomTagDefinition) => void;
    onEdit: (definition: CustomTagDefinition) => void;
  };
}

export default class SettingsCustomPropertyTagsTableDefinitions extends Component<Signature> {
  @service declare intl: Services['intl'];

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.property_tags.custom_tags.tag_name'),
        valuePath: 'name',
        textAlign: 'left' as const,
        minWidth: 200,
      },
      {
        name: this.intl.t('settings.property_tags.custom_tags.tag_type'),
        valuePath: 'tagType',
        tdComponent: SettingsCustomPropertyTagsCellsTdTagType,
        textAlign: 'left' as const,
      },
      {
        name: this.intl.t('settings.property_tags.custom_tags.values'),
        valuePath: 'values',
        textAlign: 'left' as const,
      },
      {
        isSortable: false,
        name: '',
        tdComponent: SettingsCustomPropertyTagsCellsTdDefinitionActions,
        textAlign: 'right' as const,
        valuePath: 'actions',
      },
    ];
  }

  @cached
  get rows(): CustomEmberTableRow[] {
    return this.args.definitions.map((def) => ({
      id: def.id,
      name: def.name,
      tagType: def.tagType,
      values:
        def.tagType === 'list'
          ? def.options.map((o) => o.label).join(', ')
          : this.intl.t('general.missing'),
      actions: {
        onEdit: (): void => this.args.onEdit(def),
        onDelete: (): void => this.args.onDelete(def),
      },
    }));
  }

  <template>
    <PrismCard @contentLayout="noPadding" class="h-full overflow-hidden">
      <:header>
        <span class="text-200--regular text-emphasis">{{t
            "settings.property_tags.custom_tags.tags_count"
            count=@definitions.length
            limit=MAX_CUSTOM_TAG_DEFINITIONS
          }}</span>
      </:header>
      <:content>
        <PrismTable
          @columns={{this.columns}}
          @isLoading={{@isLoading}}
          @removeTableBorder={{true}}
          @renderAll={{false}}
          @rows={{this.rows}}
          data-test-id="table-definitions"
        >
          <:emptyState>
            <div class="flex h-full w-full items-center justify-center">
              <PrismMessage
                @description={{t
                  "settings.property_tags.custom_tags.empty.description"
                }}
                @illustration="100x100_empty"
                @title={{t "settings.property_tags.custom_tags.empty.title"}}
                data-test-id="empty-state"
              />
            </div>
          </:emptyState>
        </PrismTable>
      </:content>
    </PrismCard>
  </template>
}
